import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilePdf,
  faSave, faTimes, faSpinner, faLightbulb, faChartBar, faBookOpen, faCheckCircle, faFileWord,
} from '@fortawesome/free-solid-svg-icons';
// import/no-extraneous-dependencies
// import/no-extraneous-dependencies
import { useCreatePublicationMutation, useEditPublicationMutation } from '../../../../redux/apiSlice';
import { decrypt } from '../../../../encryption/Encryption';
import RichTextEditor from '../../../components/FormsElements/RickTextEditor';
import FileUpload from '../../../components/FormsElements/FileUpload';

function PublicationForm({ publicationData }) {
  const [publicationDetails, setpublicationDetails] = useState({
    postContent: '',
    postTitle: '',
  });

  const [errors, setErrors] = useState({
    content: '',
    title: '',
  });

  const [postAuthor, setPostAuthor] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isUserAuthenticated = sessionStorage.getItem('ans-sms') || '';
  const navigate = useNavigate();
  const [createpublication] = useCreatePublicationMutation();
  const [editPublication] = useEditPublicationMutation();

  useEffect(() => {
    if (publicationData) {
      setpublicationDetails({
        postTitle: publicationData.post_title || '',
        postContent: publicationData.post_content || '',
      });
    }
  }, [publicationData]);

  useEffect(() => {
    if (isUserAuthenticated === '') {
      sessionStorage.removeItem('ans-sms');
      navigate('/login', {
        state: {
          warning: 'Please login to access the page!',
        },
      });
    }
  }, [isUserAuthenticated, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const decryptedUserInfo = await decrypt(isUserAuthenticated);
        if (decryptedUserInfo && decryptedUserInfo.token) {
          setPostAuthor(+decryptedUserInfo.user);
        } else {
          console.error('Invalid user information');
        }
      } catch (error) {
        console.error('Error while decrypting user information:', error);
      }
    };

    fetchData();
  }, [isUserAuthenticated]);

  const handlePdfUpload = () => {
    console.log('pdf');
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const newErrors = {};
    if (publicationDetails.postTitle.trim() === '') {
      newErrors.title = 'Title is required';
    }
    if (publicationDetails.postContent.trim() === '') {
      newErrors.content = 'Content is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    setErrors({
      content: '',
      title: '',
    });

    const currentDate = new Date().toISOString();
    const formData = {
      post_author: postAuthor,
      post_date: publicationData ? publicationData.post_date : currentDate,
      post_title: publicationDetails.postTitle,
      post_name: publicationDetails.postTitle,
      post_content: publicationDetails.postContent,
      guid: publicationData ? publicationData.guid : 'string',
      modified: currentDate,
    };

    try {
      const decryptedUserInfo = await decrypt(isUserAuthenticated);
      if (!decryptedUserInfo || !decryptedUserInfo.token) {
        console.error('Invalid user information');
        setIsSubmitting(false);
        return;
      }

      if (publicationData) {
        await editPublication({ publicationId: publicationData.id, updates: formData });
        setSuccessMsg('Publication updated successfully');
      } else {
        await createpublication({ ...formData });
        setSuccessMsg('Publication added successfully');
      }
      setTimeout(() => {
        setSuccessMsg('');
        setpublicationDetails({
          postContent: '',
          postTitle: '',
        });
        navigate('/private/publications');
      }, 2000);
    } catch (error) {
      console.error('Error publishing publication:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setpublicationDetails((prevDetails) => ({
      ...prevDetails,
      [id]: value,
    }));
  };

  const handleContentChange = (content) => {
    setpublicationDetails((prevDetails) => ({
      ...prevDetails,
      postContent: content,
    }));
  };

  const handleCancel = () => {
    navigate('/private/publications');
  };

  // Calculate statistics
  const wordCount = publicationDetails.postContent.replace(/<[^>]*>/g, '').split(/\s+/).filter((word) => word.length > 0).length;
  const characterCount = publicationDetails.postContent.replace(/<[^>]*>/g, '').length;
  const titleLength = publicationDetails.postTitle.length;
  const isTitleGood = titleLength >= 10 && titleLength <= 60;
  const isContentGood = wordCount >= 100;

  return (
    <div className="p-6 w-full min-h-screen bg-gray-900">
      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
            {/* Header Section */}
            <div className="bg-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-600 rounded-lg">
                    <FontAwesomeIcon icon={faFilePdf} className="text-white text-lg" />
                  </div>
                  <div>
                    <h1 className="text-xl font-semibold text-white">
                      {publicationData ? 'Edit Publication' : 'Create New Publication'}
                    </h1>
                    <p className="text-gray-300 text-sm">
                      {publicationData ? 'Update publication content and details' : 'Upload and publish a new SMS document'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="flex items-center space-x-2 px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-sm" />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Success Message */}
            {successMsg && (
              <div className="mx-6 mt-4 bg-green-900/20 border border-green-700 text-green-300 px-4 py-3 rounded-lg">
                <p className="text-center">{successMsg}</p>
              </div>
            )}

            {/* Form Content */}
            <div className="p-6">
              <form onSubmit={handlePublish} className="space-y-6">
                {/* Publication Title */}
                <div className="bg-gray-750 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-white mb-4">Publication Information</h3>
                  <div>
                    <label htmlFor="postTitle" className="block text-sm font-medium text-gray-300 mb-2">
                      Publication Title
                      {' '}
                      <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="postTitle"
                      name="postTitle"
                      value={publicationDetails.postTitle}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:outline-none"
                      placeholder="Enter the publication title..."
                    />
                    {errors.title && (
                      <p className="text-red-400 text-sm mt-1">{errors.title}</p>
                    )}
                  </div>
                </div>

                {/* Publication Content */}
                <div className="bg-gray-750 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-white mb-4">Publication Content</h3>
                  <div>
                    <label htmlFor="content" className="block text-sm font-medium text-gray-300 mb-2">
                      Content
                      {' '}
                      <span className="text-red-400">*</span>
                    </label>
                    <div className="bg-gray-700 rounded-lg border border-gray-600">
                      <RichTextEditor
                        label=""
                        name="content"
                        value={publicationDetails.postContent}
                        onChange={handleContentChange}
                        type="text"
                      />
                    </div>
                    {errors.content && (
                      <p className="text-red-400 text-sm mt-1">{errors.content}</p>
                    )}
                  </div>
                </div>

                {/* File Upload */}
                <div className="bg-gray-750 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-white mb-4">Document Upload</h3>
                  <FileUpload
                    label="Upload Document"
                    name="document"
                    onChange={handlePdfUpload}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-600">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} />
                        <span>{publicationData ? 'Update Publication' : 'Publish Publication'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Publication Assistant */}
        <div className="w-80 space-y-6">
          {/* Publication Statistics */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-600 rounded-lg">
                <FontAwesomeIcon icon={faChartBar} className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Publication Statistics</h3>
                <p className="text-gray-300 text-sm">Track your progress</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faFilePdf} className="text-green-400" />
                  <span className="text-gray-300">Title Length</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-white">{titleLength}</span>
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className={`text-sm ${isTitleGood ? 'text-green-400' : 'text-yellow-400'}`}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faBookOpen} className="text-green-400" />
                  <span className="text-gray-300">Word Count</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-white">{wordCount}</span>
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className={`text-sm ${isContentGood ? 'text-green-400' : 'text-yellow-400'}`}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faChartBar} className="text-purple-400" />
                  <span className="text-gray-300">Characters</span>
                </div>
                <span className="text-lg font-bold text-white">{characterCount}</span>
              </div>
            </div>
          </div>

          {/* Publication Guidelines */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-yellow-600 rounded-lg">
                <FontAwesomeIcon icon={faLightbulb} className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Publication Guidelines</h3>
                <p className="text-gray-300 text-sm">Best practices for documents</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-gray-750 rounded-lg">
                <h4 className="text-sm font-medium text-white mb-2">Title Guidelines</h4>
                <ul className="text-xs text-gray-300 space-y-1">
                  <li>• Keep titles between 10-60 characters</li>
                  <li>• Use clear, descriptive language</li>
                  <li>• Include document type in title</li>
                </ul>
              </div>

              <div className="p-3 bg-gray-750 rounded-lg">
                <h4 className="text-sm font-medium text-white mb-2">Content Guidelines</h4>
                <ul className="text-xs text-gray-300 space-y-1">
                  <li>• Aim for at least 100 words</li>
                  <li>• Use clear, professional language</li>
                  <li>• Include safety-related information</li>
                  <li>• Add relevant attachments</li>
                </ul>
              </div>

              <div className="p-3 bg-gray-750 rounded-lg">
                <h4 className="text-sm font-medium text-white mb-2">File Types</h4>
                <div className="flex items-center space-x-4 text-xs text-gray-300">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faFilePdf} className="text-red-400" />
                    <span>PDF</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faFileWord} className="text-blue-400" />
                    <span>Word</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-purple-600 rounded-lg">
                <FontAwesomeIcon icon={faFilePdf} className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Quick Actions</h3>
                <p className="text-gray-300 text-sm">Common tasks</p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => navigate('/private/publications')}
                className="w-full flex items-center space-x-3 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faFilePdf} className="text-gray-300" />
                <span className="text-gray-300 font-medium">View All Publications</span>
              </button>

              <button
                type="button"
                onClick={() => window.open('https://www.faa.gov/safety/programs_initiatives/aircraft_aviation/sms', '_blank')}
                className="w-full flex items-center space-x-3 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faBookOpen} className="text-gray-300" />
                <span className="text-gray-300 font-medium">SMS Resources</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

PublicationForm.propTypes = {
  publicationData: PropTypes.shape({
    id: PropTypes.number,
    post_title: PropTypes.string,
    post_content: PropTypes.string,
    post_date: PropTypes.string,
    guid: PropTypes.string,
  }),
};

PublicationForm.defaultProps = {
  publicationData: null,
};

export default PublicationForm;
